import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../common/DashboardLayout";
import ProfileServices from "../../../services/ProfileServices";
import AuthContext from "../../../context/AuthContext";
import { User, Mail, MapPin, Phone } from "react-feather";
import { showErrorAlerts, showsuccessAlert } from "../../../services/AlertService";

/**
 * Profile Page
 * Allows users to update their profile information
 */
const Profile = () => {
  const { user, userrole } = useContext(AuthContext);
  const navigate = useNavigate();
  const userobj = JSON.parse(localStorage.getItem("user") || "{}");
  
  // Ensure we have valid role and userType
  // Get role from multiple sources with proper fallback
  let role = userrole;
  if (!role || role === undefined) {
    role = userobj?.role;
  }
  if (!role || role === undefined) {
    role = user?.role;
  }
  
  // Convert string role to number if needed
  if (typeof role === 'string') {
    if (role === 'admin' || role === 'Admin') {
      role = 'admin';
    } else if (role === '1' || role === 'school') {
      role = 1;
    } else if (role === '2' || role === 'schoolteacher') {
      role = 2;
    } else if (role === '3' || role === 'teacher') {
      role = 3;
    } else {
      const parsed = parseInt(role);
      role = isNaN(parsed) ? 1 : parsed;
    }
  }
  
  // Default to 1 (school admin) if still no role
  if (!role || role === undefined || role === null) {
    role = 1;
  }
  
  const userType = userobj?.type || user?.type;
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    phone: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setFetching(true);
      const response = await ProfileServices.getMyProfile();
      if (response && response.success && response.profile) {
        const profile = response.profile;
        setFormData({
          name: profile.name || "",
          email: profile.email || "",
          address: profile.address || "",
          city: profile.city || "",
          state: profile.state || "",
          zipCode: profile.zipCode || "",
          phone: profile.phone || "",
        });
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      showErrorAlerts("Failed to load profile information");
    } finally {
      setFetching(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field) => (e) => {
    let value = e.target.value;
    
    // Auto-uppercase state field
    if (field === 'state') {
      value = value.toUpperCase();
    }
    
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await ProfileServices.updateMyProfile({
        name: formData.name.trim(),
        email: formData.email.trim(),
        address: formData.address.trim(),
        city: formData.city.trim(),
        state: formData.state.trim(),
        zipCode: formData.zipCode.trim(),
        phone: formData.phone.trim(),
      });

      if (response && response.success) {
        showsuccessAlert("Profile updated successfully!");
        
        // Update localStorage with new profile data
        if (response.profile) {
          // Get existing user data from localStorage
          const existingUserStr = localStorage.getItem("user");
          const existingUser = existingUserStr ? JSON.parse(existingUserStr) : {};
          
          // Merge updated profile data
          const updatedUser = {
            ...existingUser,
            name: response.profile.name,
            email: response.profile.email,
            address: response.profile.address,
            city: response.profile.city,
            state: response.profile.state,
            zipCode: response.profile.zipCode,
            phone: response.profile.phone,
          };
          
          // Update localStorage
          localStorage.setItem("user", JSON.stringify(updatedUser));
        }
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to update profile";
      showErrorAlerts(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <DashboardLayout
        role={Number(role) || 1}
        userType={userType}
        activeMenu="Profile"
        layoutType="topnav"
      >
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 text-muted">Loading profile...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      role={Number(role) || 1}
      userType={userType}
      activeMenu="Profile"
      layoutType="topnav"
    >
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center mb-4">
                  <User size={24} className="me-2" />
                  <h4 className="mb-0">My Profile</h4>
                </div>
                <p className="text-muted">
                  Update your profile information below. All fields marked with
                  * are required.
                </p>

                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="name" className="form-label">
                        <User size={16} className="me-1" />
                        Full Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${
                          errors.name ? "is-invalid" : ""
                        }`}
                        id="name"
                        value={formData.name}
                        onChange={handleInputChange("name")}
                        placeholder="Enter your full name"
                        disabled={loading}
                        required
                      />
                      {errors.name && (
                        <div className="invalid-feedback">{errors.name}</div>
                      )}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="email" className="form-label">
                        <Mail size={16} className="me-1" />
                        Email Address <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className={`form-control ${
                          errors.email ? "is-invalid" : ""
                        }`}
                        id="email"
                        value={formData.email}
                        onChange={handleInputChange("email")}
                        placeholder="Enter your email address"
                        disabled={loading}
                        required
                      />
                      {errors.email && (
                        <div className="invalid-feedback">{errors.email}</div>
                      )}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="phone" className="form-label">
                        <Phone size={16} className="me-1" />
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        className="form-control"
                        id="phone"
                        value={formData.phone}
                        onChange={handleInputChange("phone")}
                        placeholder="Enter your phone number"
                        disabled={loading}
                      />
                    </div>

                    <div className="col-md-12 mb-3">
                      <label htmlFor="address" className="form-label">
                        <MapPin size={16} className="me-1" />
                        Street Address
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="address"
                        value={formData.address}
                        onChange={handleInputChange("address")}
                        placeholder="Enter your Street Address"
                        disabled={loading}
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <label htmlFor="city" className="form-label">
                        City
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="city"
                        value={formData.city}
                        onChange={handleInputChange("city")}
                        placeholder="Enter your city"
                        disabled={loading}
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <label htmlFor="state" className="form-label">
                        State
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="state"
                        value={formData.state}
                        onChange={handleInputChange("state")}
                        placeholder="Enter your state"
                        disabled={loading}
                        maxLength={2}
                        style={{ textTransform: 'uppercase' }}
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <label htmlFor="zipCode" className="form-label">
                        Zip Code
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="zipCode"
                        value={formData.zipCode}
                        onChange={handleInputChange("zipCode")}
                        placeholder="Enter your zip code"
                        disabled={loading}
                        maxLength={10}
                      />
                    </div>
                  </div>

                  <div className="d-flex gap-2 mt-4">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Updating...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Update Profile
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => navigate("/main-dashboard")}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Profile;

